import { getLenis } from './lenis';

const SCROLL_LOCK_PANEL_SELECTOR =
    '[data-scroll-lock-panel], .minicart-sidebar__body, .listing__filters-sidebar-body';
const MAP_SCROLL_LOCK_SELECTOR = '[js-map-shell], [js-map-wrapper]';

const getState = () => {
    if (!window.__mdaPageScrollLock) {
        window.__mdaPageScrollLock = {
            count: 0,
            pageScrollState: null,
            galleryPlaceholders: [],
            headerMenuScrollLocked: false,
            headerScrollLockInitialized: false,
            listenersAttached: false,
        };
    }

    return window.__mdaPageScrollLock;
};

const getScrollLockPanel = target =>
    target?.closest?.(SCROLL_LOCK_PANEL_SELECTOR) ?? null;

const isMapScrollTarget = target =>
    Boolean(target?.closest?.(MAP_SCROLL_LOCK_SELECTOR));

const canScrollPanel = (panel, deltaY = 0) => {
    if (!panel || panel.scrollHeight <= panel.clientHeight + 1) {
        return false;
    }

    if (deltaY < 0) {
        return panel.scrollTop > 0;
    }

    if (deltaY > 0) {
        return panel.scrollTop + panel.clientHeight < panel.scrollHeight - 1;
    }

    return true;
};

let activeTouchPanel = null;
const touchLastYByPanel = new WeakMap();

const handleLockedTouchStart = event => {
    activeTouchPanel = getScrollLockPanel(event.target);

    if (activeTouchPanel && event.touches[0]) {
        touchLastYByPanel.set(activeTouchPanel, event.touches[0].clientY);
    }
};

const handleLockedTouchEnd = () => {
    activeTouchPanel = null;
};

const handleLockedTouchMove = event => {
    if (isMapScrollTarget(event.target)) {
        event.preventDefault();
        return;
    }

    const panel = getScrollLockPanel(event.target) || activeTouchPanel;

    if (!panel) {
        event.preventDefault();
        return;
    }

    if (panel.scrollHeight <= panel.clientHeight + 1) {
        event.preventDefault();
        return;
    }

    const touch = event.touches[0];

    if (!touch) {
        return;
    }

    const lastY = touchLastYByPanel.get(panel);

    if (lastY === undefined) {
        touchLastYByPanel.set(panel, touch.clientY);
        return;
    }

    const deltaY = lastY - touch.clientY;

    touchLastYByPanel.set(panel, touch.clientY);

    if (!canScrollPanel(panel, deltaY)) {
        event.preventDefault();
    }
};

const handleLockedWheel = event => {
    if (isMapScrollTarget(event.target)) {
        event.preventDefault();
        return;
    }

    const panel = getScrollLockPanel(event.target);

    if (!panel) {
        event.preventDefault();
        return;
    }

    if (!canScrollPanel(panel, event.deltaY)) {
        event.preventDefault();
        return;
    }

    panel.scrollTop += event.deltaY;
    event.preventDefault();
};

const addScrollLockListeners = () => {
    const lockState = getState();

    if (lockState.listenersAttached) {
        return;
    }

    document.addEventListener('wheel', handleLockedWheel, {
        passive: false,
        capture: true,
    });
    document.addEventListener('touchstart', handleLockedTouchStart, {
        passive: true,
        capture: true,
    });
    document.addEventListener('touchmove', handleLockedTouchMove, {
        passive: false,
        capture: true,
    });
    document.addEventListener('touchend', handleLockedTouchEnd, {
        passive: true,
        capture: true,
    });
    document.addEventListener('touchcancel', handleLockedTouchEnd, {
        passive: true,
        capture: true,
    });
    lockState.listenersAttached = true;
};

const removeScrollLockListeners = () => {
    const lockState = getState();

    if (!lockState.listenersAttached) {
        return;
    }

    document.removeEventListener('wheel', handleLockedWheel, {
        capture: true,
    });
    document.removeEventListener('touchstart', handleLockedTouchStart, {
        capture: true,
    });
    document.removeEventListener('touchmove', handleLockedTouchMove, {
        capture: true,
    });
    document.removeEventListener('touchend', handleLockedTouchEnd, {
        capture: true,
    });
    document.removeEventListener('touchcancel', handleLockedTouchEnd, {
        capture: true,
    });
    lockState.listenersAttached = false;
};

const getScrollbarOffset = () =>
    window.innerWidth - document.documentElement.clientWidth;

const supportsStableScrollbarGutter = () =>
    CSS.supports('scrollbar-gutter: stable');

const GALLERY_COLUMN_SELECTOR = '[js-product-gallery-column]';
const GALLERY_PLACEHOLDER_CLASS = 'scroll-lock-gallery-placeholder';

const getPageScrollPosition = () => {
    const lenis = getLenis();
    const scrollX = window.scrollX || window.pageXOffset || 0;
    let scrollY = window.scrollY || window.pageYOffset || 0;

    if (lenis && Number.isFinite(lenis.scroll)) {
        scrollY = lenis.scroll;
    }

    return { scrollX, scrollY };
};

const pinGalleryColumns = () => {
    const lockState = getState();
    const placeholders = [];

    document.querySelectorAll(GALLERY_COLUMN_SELECTOR).forEach(element => {
        const rect = element.getBoundingClientRect();
        const computed = window.getComputedStyle(element);
        const placeholder = document.createElement('div');

        placeholder.className = GALLERY_PLACEHOLDER_CLASS;
        placeholder.setAttribute('aria-hidden', 'true');
        placeholder.style.width = `${rect.width}px`;
        placeholder.style.height = `${rect.height}px`;
        placeholder.style.flexShrink = computed.flexShrink || '0';
        placeholder.style.order = computed.order || '';
        placeholder.style.marginBottom = computed.marginBottom || '';
        placeholder.style.pointerEvents = 'none';

        element.parentElement?.insertBefore(placeholder, element);

        element.classList.add('is-scroll-lock-pinned');
        element.style.setProperty('--scroll-lock-pin-top', `${rect.top}px`);
        element.style.setProperty('--scroll-lock-pin-left', `${rect.left}px`);
        element.style.setProperty('--scroll-lock-pin-width', `${rect.width}px`);
        element.style.setProperty('--scroll-lock-pin-height', `${rect.height}px`);

        placeholders.push(placeholder);
    });

    lockState.galleryPlaceholders = placeholders;
};

const unpinGalleryColumns = () => {
    const lockState = getState();

    document.querySelectorAll(`${GALLERY_COLUMN_SELECTOR}.is-scroll-lock-pinned`).forEach(element => {
        element.classList.remove('is-scroll-lock-pinned');
        element.style.removeProperty('--scroll-lock-pin-top');
        element.style.removeProperty('--scroll-lock-pin-left');
        element.style.removeProperty('--scroll-lock-pin-width');
        element.style.removeProperty('--scroll-lock-pin-height');
    });

    lockState.galleryPlaceholders.forEach(placeholder => {
        placeholder.remove();
    });
    lockState.galleryPlaceholders = [];
};

const clearScrollLockStyles = () => {
    const root = document.documentElement;

    root.style.removeProperty('--scrollbar-compensation');
    root.style.removeProperty('--scroll-lock-offset');
};

const restorePageScroll = (scrollX, scrollY) => {
    const root = document.documentElement;
    const body = document.body;
    const prevRootBehavior = root.style.scrollBehavior;
    const prevBodyBehavior = body.style.scrollBehavior;

    root.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';

    window.scrollTo(scrollX, scrollY);

    const lenis = getLenis();

    if (lenis) {
        lenis.scrollTo(scrollY, { immediate: true });
    }

    root.style.scrollBehavior = prevRootBehavior;
    body.style.scrollBehavior = prevBodyBehavior;
};

export const lockPageScroll = () => {
    const lockState = getState();

    if (lockState.count === 0) {
        const root = document.documentElement;
        const { scrollX, scrollY } = getPageScrollPosition();
        const scrollbarOffset = getScrollbarOffset();

        lockState.pageScrollState = { scrollX, scrollY };

        pinGalleryColumns();
        root.style.setProperty('--scroll-lock-offset', `-${scrollY}px`);

        if (!supportsStableScrollbarGutter() && scrollbarOffset > 0) {
            root.style.setProperty(
                '--scrollbar-compensation',
                `${scrollbarOffset}px`
            );
        }

        root.classList.add('is-scroll-locked');
        addScrollLockListeners();
        getLenis()?.stop();
    }

    lockState.count += 1;
};

export const unlockPageScroll = () => {
    const lockState = getState();

    lockState.count = Math.max(0, lockState.count - 1);

    if (lockState.count !== 0) {
        return;
    }

    const pageScrollState = lockState.pageScrollState;
    const root = document.documentElement;

    removeScrollLockListeners();
    root.classList.remove('is-scroll-locked');
    clearScrollLockStyles();
    unpinGalleryColumns();

    if (pageScrollState) {
        restorePageScroll(pageScrollState.scrollX, pageScrollState.scrollY);
    }

    getLenis()?.start();
    lockState.pageScrollState = null;
};

const syncHeaderMenuScrollLock = () => {
    const lockState = getState();
    const panels = document.querySelectorAll('[js-menu-element]');
    const shouldLock = Array.from(panels).some(panel =>
        panel.classList.contains('-active')
    );

    if (shouldLock === lockState.headerMenuScrollLocked) {
        return;
    }

    if (shouldLock) {
        lockPageScroll();
    } else {
        unlockPageScroll();
    }

    lockState.headerMenuScrollLocked = shouldLock;
};

export const initHeaderScrollLock = () => {
    const lockState = getState();

    if (lockState.headerScrollLockInitialized) {
        return;
    }

    const panels = document.querySelectorAll('[js-menu-element]');

    if (!panels.length) {
        return;
    }

    panels.forEach(panel => {
        new MutationObserver(() => {
            syncHeaderMenuScrollLock();
        }).observe(panel, {
            attributes: true,
            attributeFilter: ['class'],
        });
    });

    syncHeaderMenuScrollLock();
    lockState.headerScrollLockInitialized = true;
};
